import React, { useState, useEffect } from 'react';
import { Search, MessageCircle, ShieldCheck } from 'lucide-react';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from '../apiEnv';

interface AdminUser {
  uid: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  isAdmin?: boolean;
}

interface AdminUserManagementProps {
  onOpenChat: (uid: string) => void;
}

const Avatar: React.FC<{ displayName: string; avatarUrl?: string }> = ({ displayName, avatarUrl }) => {
  const initial = (displayName || '?').trim().charAt(0).toUpperCase();
  if (avatarUrl) {
    return <img src={avatarUrl} alt={displayName} className="w-10 h-10 rounded-full object-cover shrink-0" />;
  }
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/70 to-blue-600/70 text-white flex items-center justify-center font-orbitron font-bold text-lg shrink-0">
      {initial}
    </div>
  );
};

const AdminUserManagement: React.FC<AdminUserManagementProps> = ({ onOpenChat }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const url = isLocalBackend() ? '/api/admin/users' : phpApiUrl('admin_get_users');
        const res = await fetch(url, { headers: phpAuthHeaders(), cache: 'no-store' });
        const text = await res.text();
        let data: any = null;
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error(`服务异常 (${res.status})，请重启本地服务后重试`);
        }
        if (res.ok && data.success) {
          setUsers(Array.isArray(data.users) ? data.users : []);
        } else {
          setError(data.error || 'Failed to load users');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = users.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.uid.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/35 px-3 py-2.5">
        <Search size={16} className="text-zinc-500 shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜索用户名 / 邮箱 / UID"
          className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none"
        />
      </div>

      <div className="flex items-center justify-between text-xs text-zinc-500 font-orbitron">
        <span>共 {filtered.length} 位用户</span>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-12 font-orbitron animate-pulse">加载中...</div>
      ) : error ? (
        <div className="text-center text-red-400 py-12 font-orbitron">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-zinc-500 py-12 font-orbitron">未找到用户</div>
      ) : (
        <ul className="divide-y divide-white/5 rounded-xl border border-white/10 bg-black/35 overflow-hidden">
          {filtered.map((u) => (
            <li key={u.uid} className="flex items-center gap-3 px-3 md:px-4 py-3 hover:bg-white/5 transition-colors">
              <Avatar displayName={u.displayName} avatarUrl={u.avatarUrl} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-pingfang text-sm md:text-base text-white truncate">{u.displayName}</span>
                  {u.isAdmin && (
                    <span className="shrink-0 inline-flex items-center gap-0.5 rounded bg-cyan-500/20 px-1.5 py-0.5 text-[10px] font-orbitron text-cyan-300">
                      <ShieldCheck size={11} />
                      ADMIN
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate">{u.email} · UID {u.uid}</div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChat(u.uid)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-cyan-500/15 border border-cyan-400/30 px-3 py-1.5 text-xs font-pingfang text-cyan-300 hover:bg-cyan-500/25 hover:text-cyan-200 transition-colors"
              >
                <MessageCircle size={14} />
                私聊
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminUserManagement;
